import React, { useState, useEffect } from 'react';
import apiClient from '../../api/apiClient';
import { Plus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import RecruitmentStatsCards from '../../components/hris/recruitment/RecruitmentStatsCards';
import RecruitmentTable from '../../components/hris/recruitment/RecruitmentTable';
import RecruitmentFormDialog from '../../components/hris/recruitment/RecruitmentFormDialog';
import DeleteRecruitmentDialog from '../../components/hris/recruitment/DeleteRecruitmentDialog';
import OvertimeDialog from '../../components/hris/recruitment/OvertimeDialog';
import { parseRupiah } from '../../components/hris/recruitment/utils/formatRupiah';

export default function RecruitmentList() {
  const [recruitments, setRecruitments] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecruitment, setEditingRecruitment] = useState(null);
  const [deleteRecruitmentId, setDeleteRecruitmentId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Overtime dialog states
  const [overtimeDialogOpen, setOvertimeDialogOpen] = useState(false);
  const [recruitmentForm, setRecruitmentForm] = useState({
    department_id: '' as string | number,
    position_id: '' as string | number,
    number_of_positions: 1,
    employment_type: 'permanent',
    justification: '',
    requirements: '',
    expected_start_date: '',
    budget: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const data = await apiClient.get('/hris/recruitment');
      setRecruitments(data.recruitments || []);
      setDepartments(data.departments || []);
      setPositions(data.positions || []);
    } catch (error) {
      // Error loading data
    } finally {
      setLoading(false);
    }
  };

  const getDepartmentName = (deptId: number) => {
    return departments.find((d: any) => d.id === deptId)?.name || '-';
  };

  const getPositionName = (posId: number) => {
    return positions.find((p: any) => p.id === posId)?.name || '-';
  };

  const openDialog = (recruitment: any = null) => {
    if (recruitment) {
      setEditingRecruitment(recruitment);
      setRecruitmentForm({
        department_id: recruitment.department_id || '',
        position_id: recruitment.position_id || '',
        number_of_positions: recruitment.number_of_positions || 1,
        employment_type: recruitment.employment_type || 'permanent',
        justification: recruitment.justification || '',
        requirements: recruitment.requirements || '',
        expected_start_date: recruitment.expected_start_date || '',
        budget: recruitment.budget ? recruitment.budget.toString() : '',
      });
    } else {
      setEditingRecruitment(null);
      setRecruitmentForm({
        department_id: '',
        position_id: '',
        number_of_positions: 1,
        employment_type: 'permanent',
        justification: '',
        requirements: '',
        expected_start_date: '',
        budget: '',
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!recruitmentForm.department_id || !recruitmentForm.position_id) return;

    setSaving(true);
    try {
      const dataToSave = {
        ...recruitmentForm,
        budget: recruitmentForm.budget ? parseFloat(parseRupiah(recruitmentForm.budget)) : null,
        status: editingRecruitment ? (editingRecruitment as any).status : 'pending',
      };

      if (editingRecruitment) {
        await apiClient.put(`/hris/recruitment/${(editingRecruitment as any).id}`, dataToSave);
      } else {
        await apiClient.post('/hris/recruitment', dataToSave);
      }
      loadData();
      setDialogOpen(false);
    } catch (error) {
      // Error saving recruitment
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteRecruitmentId) return;
    try {
      await apiClient.delete(`/hris/recruitment/${deleteRecruitmentId}`);
      loadData();
      setDeleteRecruitmentId(null);
    } catch (error) {
      // Error deleting recruitment
    }
  };

  const handleStatusChange = async (rec: any, newStatus: string) => {
    try {
      await apiClient.put(`/hris/recruitment/${rec.id}`, {
        status: newStatus,
        approved_date: newStatus === 'approved' ? new Date().toISOString().split('T')[0] : null,
      });
      loadData();
    } catch (error) {
      // Error updating status
    }
  };

  // const handleAddOvertime = (rec: any) => {
  //   setSelectedRecruitmentForOvertime(rec);
  //   setOvertimeDialogOpen(true);
  // };

  // const handleSaveOvertime = async (overtimeData: any) => {
  //   setSaving(true);
  //   try {
  //     // Save overtime data (adjust endpoint as needed)
  //     await apiClient.post('/hris/overtime', overtimeData);
  //     loadData();
  //     setOvertimeDialogOpen(false);
  //     setSelectedRecruitmentForOvertime(null);
  //   } catch (error) {
  //     // Error saving overtime
  //   } finally {
  //     setSaving(false);
  //   }
  // };

  // const getOvertimeForRecruitment = (recId: number) => {
  //   const recruitmentOvertimes = overtimes.filter((ot: any) => ot.recruitment_id === recId);
  //   if (recruitmentOvertimes.length === 0) return null;
  //   const totalHours = recruitmentOvertimes.reduce(
  //     (sum: number, ot: any) => sum + (ot.duration_hours || 0),
  //     0
  //   );
  //   const totalAmount = recruitmentOvertimes.reduce(
  //     (sum: number, ot: any) => sum + (ot.total_amount || 0),
  //     0
  //   );
  //   const latestOvertime = recruitmentOvertimes[recruitmentOvertimes.length - 1];
  //   const status = latestOvertime?.status || 'pending';
  //   return {
  //     total_hours: totalHours,
  //     total_amount: totalAmount,
  //     status: status,
  //     count: recruitmentOvertimes.length,
  //   };
  // };

  const stats = {
    pending: recruitments.filter((r: any) => r.status === 'pending').length,
    approved: recruitments.filter((r: any) => r.status === 'approved').length,
    inProgress: recruitments.filter((r: any) => r.status === 'in_progress').length,
    completed: recruitments.filter((r: any) => r.status === 'completed').length,
  };

  const filteredRecruitments = recruitments.filter((rec: any) => {
    if (filterStatus === 'all') return true;
    return rec.status === filterStatus;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Pengadaan Karyawan</h1>
          <p className="text-gray-500 mt-1">Kelola permintaan rekrutmen karyawan baru</p>
        </div>
        <Button onClick={() => openDialog()} className="bg-emerald-600 hover:bg-emerald-700">
          <Plus className="w-4 h-4 mr-2" /> Ajukan Rekrutmen
        </Button>
      </div>

      {/* Stats */}
      <RecruitmentStatsCards stats={stats} />

      {/* Table */}
      <RecruitmentTable
        recruitments={filteredRecruitments}
        loading={loading}
        filterStatus={filterStatus}
        onFilterChange={setFilterStatus}
        getDepartmentName={getDepartmentName}
        getPositionName={getPositionName}
        onStatusChange={handleStatusChange}
        onEdit={openDialog}
        onDelete={setDeleteRecruitmentId}
      />

      {/* Form Dialog */}
      <RecruitmentFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editingRecruitment}
        form={recruitmentForm}
        setForm={setRecruitmentForm}
        departments={departments}
        positions={positions}
        saving={saving}
        onSave={handleSave}
      />

      {/* Delete Dialog */}
      <DeleteRecruitmentDialog
        open={!!deleteRecruitmentId}
        onOpenChange={() => setDeleteRecruitmentId(null)}
        onConfirm={handleDelete}
      />

      {/* Overtime Dialog */}
      <OvertimeDialog
        open={overtimeDialogOpen}
        onOpenChange={setOvertimeDialogOpen}
        recruitment={null}
        saving={saving}
        onSave={() => {}}
      />
    </div>
  );
}
